import React, { useState, useEffect } from "react";
import "./App.css";
import CinematicLanding from "./components/CinematicLanding";
import MissionLanding from "./components/MissionLanding";
import HowItWorksLanding from "./components/HowItWorksLanding";
import PathwayGateway from "./components/PathwayGateway";
import TerminalInterface from "./components/TerminalInterface";
import DefianceCreeksideInterface from "./components/DefianceCreeksideInterface";
import WorkingPaperDisplay from "./components/WorkingPaperDisplay";
import WorkingPapersArchive from "./components/WorkingPapersArchive";
import AdminDashboard from "./components/AdminDashboard";
import SonicExperience from "./components/SonicExperience";
import AboutPage from "./components/AboutPage";
import globalHeartbeat from "./utils/globalHeartbeatAudio";
import { handleBelongingSessionsSSO, getBelongingUser } from "./utils/belongingSessionsSSO";

// Initialize heartbeat globally
if (typeof window !== 'undefined') {
  window.planetaryFedHeartbeat = window.planetaryFedHeartbeat || globalHeartbeat;
}

function App() {
  const [currentView, setCurrentView] = useState('cinematic'); // Start with cinematic landing
  const [currentPaperId, setCurrentPaperId] = useState('today');
  const [interfaceChoice, setInterfaceChoice] = useState(null);
  const [hasSeenCinematic, setHasSeenCinematic] = useState(false);
  const [hasSeenMission, setHasSeenMission] = useState(false);
  
  // SHARED CONVERSATION STATE - Persists across Terminal ↔ Creekside
  const [sharedConversation, setSharedConversation] = useState({
    history: [],
    orbParams: { energy: 0.5, warmth: 0.6, complexity: 0.5, harmony: 0.7, expansion: 0.5 }
  });

  // Initialize heartbeat on mount
  useEffect(() => {
    if (window.planetaryFedHeartbeat) {
      window.planetaryFedHeartbeat.setEnabled(true);
    }
    
    // Check URL for view parameter FIRST
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');
    
    if (viewParam === 'about') {
      setCurrentView('about');
      setHasSeenCinematic(true);
      return;
    }
    
    // Check if user has seen cinematic + has choice/covenant
    const seenCinematic = sessionStorage.getItem('mairy_seen_cinematic');
    const savedChoice = localStorage.getItem('mairy_interface_choice');
    const covenantAccepted = localStorage.getItem('mairy_covenant_accepted');
    
    if (seenCinematic) {
      setHasSeenCinematic(true);
      // Check for SSO or existing session
      handleBelongingSessionsSSO().then(ssoUser => {
        if (ssoUser) {
          // SSO user - go to their saved interface or gateway
          if (savedChoice) {
            setInterfaceChoice(savedChoice);
            setCurrentView(savedChoice);
          } else {
            setCurrentView('gateway');
          }
        } else if (savedChoice && covenantAccepted === 'true') {
          // Returning user with choice - skip to interface
          setInterfaceChoice(savedChoice);
          setCurrentView(savedChoice);
        } else {
          // Go to gateway
          setCurrentView('gateway');
        }
      });
    }
  }, []);

  const handleEnterFromCinematic = () => {
    sessionStorage.setItem('mairy_seen_cinematic', 'true');
    setHasSeenCinematic(true);
    
    // Check URL for view parameter
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');
    
    if (viewParam === 'about') {
      setCurrentView('about');
      return;
    }
    
    // Go to mission landing (second page)
    setCurrentView('mission');
  };
  
  const handleContinueFromMission = () => {
    sessionStorage.setItem('mairy_seen_mission', 'true');
    setHasSeenMission(true);
    setCurrentView('howitworks');
  };
  
  const handleContinueFromHowItWorks = () => {
    sessionStorage.setItem('mairy_seen_howitworks', 'true');
    
    // Check for existing session
    const savedChoice = localStorage.getItem('mairy_interface_choice');
    const covenant = localStorage.getItem('mairy_covenant_accepted');
    
    if (savedChoice && covenant === 'true') {
      setInterfaceChoice(savedChoice);
      setCurrentView(savedChoice);
    } else {
      setCurrentView('gateway');
    }
  };

  // Navigation handlers
  const handleNavigateToTerminal = () => setCurrentView('terminal');
  const handleNavigateToCreekside = () => setCurrentView('creekside');
  const handleNavigateToGateway = () => setCurrentView('gateway');
  const handleNavigateToPaper = (paperId = 'today') => {
    setCurrentPaperId(paperId);
    setCurrentView('paper');
  };
  const handleNavigateToArchive = () => setCurrentView('archive');
  const handleNavigateToAdmin = () => setCurrentView('admin');
  const handleNavigateToSonic = () => setCurrentView('sonic');
  const handleNavigateToAbout = () => setCurrentView('about');
  
  const handleChooseTerminal = () => {
    setInterfaceChoice('terminal');
    localStorage.setItem('mairy_interface_choice', 'terminal');
    setCurrentView('terminal');
  };
  
  const handleChooseCreekside = () => {
    setInterfaceChoice('creekside');
    localStorage.setItem('mairy_interface_choice', 'creekside');
    setCurrentView('creekside');
  };

  // Render current view
  const renderView = () => {
    // Always show cinematic first on fresh sessions
    if (!hasSeenCinematic) {
      return <CinematicLanding onEnter={handleEnterFromCinematic} />;
    }
    
    // Then show mission landing
    if (!hasSeenMission && currentView !== 'about') {
      return <MissionLanding onContinue={handleContinueFromMission} />;
    }

    switch (currentView) {
      case 'cinematic':
        return <CinematicLanding onEnter={handleEnterFromCinematic} />;
      
      case 'mission':
        return <MissionLanding onContinue={handleContinueFromMission} />;
      
      case 'howitworks':
        return <HowItWorksLanding onContinue={handleContinueFromHowItWorks} />;
      
      case 'gateway':
        return (
          <PathwayGateway
            onChooseTerminal={handleChooseTerminal}
            onChooseCreekside={handleChooseCreekside}
          />
        );
      
      case 'terminal':
        return (
          <TerminalInterface 
            onNavigateToAdmin={handleNavigateToAdmin}
            onNavigateToPaper={handleNavigateToPaper}
            onNavigateToArchive={handleNavigateToArchive}
            onNavigateToCreekside={handleNavigateToCreekside}
            onNavigateToSonic={handleNavigateToSonic}
            sharedConversation={sharedConversation}
            setSharedConversation={setSharedConversation}
          />
        );
      
      case 'creekside':
        return (
          <DefianceCreeksideInterface
            onNavigateToPaper={handleNavigateToPaper}
            onNavigateToArchive={handleNavigateToArchive}
            onNavigateBack={handleNavigateToGateway}
            onNavigateToTerminal={handleNavigateToTerminal}
            sharedConversation={sharedConversation}
            setSharedConversation={setSharedConversation}
          />
        );
      
      case 'paper':
        return (
          <WorkingPaperDisplay 
            paperId={currentPaperId}
            onBack={interfaceChoice === 'creekside' ? handleNavigateToCreekside : handleNavigateToTerminal}
            onNavigateToArchive={handleNavigateToArchive}
            onNavigateToCreekside={interfaceChoice === 'terminal' ? handleNavigateToCreekside : null}
          />
        );
      
      case 'archive':
        return (
          <WorkingPapersArchive 
            onBack={interfaceChoice === 'creekside' ? handleNavigateToCreekside : handleNavigateToTerminal}
            onViewPaper={handleNavigateToPaper}
          />
        );
      
      case 'admin':
        return (
          <AdminDashboard 
            onBack={handleNavigateToTerminal}
          />
        );
      
      case 'sonic':
        return (
          <SonicExperience 
            player={{ name: 'Traveler' }}
            onNavigateToGame={handleNavigateToTerminal}
            onNavigateHome={handleNavigateToTerminal}
          />
        );
      
      case 'about':
        return (
          <AboutPage 
            onBack={() => {
              if (interfaceChoice === 'creekside') {
                handleNavigateToCreekside();
              } else if (interfaceChoice === 'terminal') {
                handleNavigateToTerminal();
              } else {
                handleNavigateToGateway();
              }
            }}
          />
        );
      
      default:
        return (
          <PathwayGateway
            onChooseTerminal={handleChooseTerminal}
            onChooseCreekside={handleChooseCreekside}
          />
        );
    }
  };

  return (
    <div className="App">
      {renderView()}
    </div>
  );
}

export default App;
